import React from "react";

export interface PanZoomState {
  zoom: number;
  pan: { x: number; y: number };
  isPanning: boolean;
  onCanvasMouseDown: (e: React.MouseEvent<HTMLDivElement>) => void;
  setZoom: React.Dispatch<React.SetStateAction<number>>;
  setPan: React.Dispatch<React.SetStateAction<{ x: number; y: number }>>;
  resetView: () => void;
}

const clamp = (value: number, min: number, max: number) =>
  Math.max(min, Math.min(max, value));

export function usePanZoom(initialZoom: number = 1): PanZoomState {
  const [zoom, setZoom] = React.useState<number>(initialZoom);
  const [pan, setPan] = React.useState<{ x: number; y: number }>({
    x: 0,
    y: 0,
  });
  const [isPanning, setIsPanning] = React.useState(false);
  const lastPanRef = React.useRef<{ x: number; y: number } | null>(null);

  const resetView = React.useCallback(() => {
    setZoom(1);
    setPan({ x: 0, y: 0 });
  }, []);

  const onCanvasMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    // Middle-click or Alt+click anywhere inside the canvas starts panning
    if (e.button === 1 || e.altKey) {
      setIsPanning(true);
      lastPanRef.current = { x: e.clientX, y: e.clientY };
      document.body.style.cursor = "grabbing";
      return;
    }
    // Left-click: only pan when not clicking on a node card
    if (e.button === 0) {
      const target = e.target as HTMLElement;
      const onNodeCard = !!target.closest('[data-node-card="1"]');
      if (!onNodeCard) {
        setIsPanning(true);
        lastPanRef.current = { x: e.clientX, y: e.clientY };
        document.body.style.cursor = "grabbing";
      }
    }
  };

  React.useEffect(() => {
    if (!isPanning) return;
    const onMove = (e: MouseEvent) => {
      if (!lastPanRef.current) return;
      const dx = e.clientX - lastPanRef.current.x;
      const dy = e.clientY - lastPanRef.current.y;
      lastPanRef.current = { x: e.clientX, y: e.clientY };
      setPan((prev) => ({ x: prev.x + dx, y: prev.y + dy }));
    };
    const onUp = () => {
      setIsPanning(false);
      lastPanRef.current = null;
      document.body.style.cursor = "";
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, [isPanning]);

  // Helpers for zooming in/out with clamping
  const setZoomClamped: React.Dispatch<React.SetStateAction<number>> = (
    updater,
  ) => {
    setZoom((prev) =>
      clamp(
        typeof updater === "function"
          ? (updater as (v: number) => number)(prev)
          : updater,
        0.3,
        2,
      ),
    );
  };

  return {
    zoom,
    pan,
    isPanning,
    onCanvasMouseDown,
    setZoom: setZoomClamped,
    setPan,
    resetView,
  };
}
